import { DAY_MS, toMillis } from './dates';

export interface MetricInvoice {
  status?: string;
  totalCharges?: number;
  driver?: string;
  createdAt?: any;
  updatedAt?: any;
}

export interface Metric {
  value: number;
  /** Change against the same metric 30 days ago; null when there is no baseline */
  delta: number | null;
  /** How `delta` should be rendered */
  deltaKind: 'percent' | 'absolute';
  /** Whether a rise in this metric should read as good (green) or bad (red) */
  riseIsGood: boolean;
}

export interface PendingMetrics {
  totalPendingAmount: Metric;
  pendingInvoiceCount: Metric;
  driversAwaitingPayout: Metric;
  averageAgingDays: Metric;
}

const isPending = (invoice: MetricInvoice) => invoice.status === 'pending';

/**
 * Which invoices were sitting in "pending" at `asOf`.
 *
 * Nothing records when an invoice left pending, so `updatedAt` stands in for it:
 * an invoice created before the cutoff was pending then if it is still pending
 * now, or if it was touched after the cutoff (i.e. it moved on later). Invoices
 * with no timestamps are left out rather than guessed at.
 */
const pendingAsOf = (invoices: MetricInvoice[], asOf: number): MetricInvoice[] =>
  invoices.filter(invoice => {
    const created = toMillis(invoice.createdAt);
    if (!created || created > asOf) return false;
    if (isPending(invoice)) return true;
    const updated = toMillis(invoice.updatedAt);
    return updated > asOf;
  });

const sumCharges = (invoices: MetricInvoice[]): number =>
  invoices.reduce((sum, invoice) => sum + (invoice.totalCharges || 0), 0);

const distinctDrivers = (invoices: MetricInvoice[]): number =>
  new Set(invoices.map(invoice => invoice.driver).filter(Boolean)).size;

const averageAging = (invoices: MetricInvoice[], asOf: number): number => {
  const ages = invoices
    .map(invoice => toMillis(invoice.createdAt))
    .filter(created => created > 0)
    .map(created => (asOf - created) / DAY_MS);
  if (ages.length === 0) return 0;
  return ages.reduce((sum, age) => sum + age, 0) / ages.length;
};

const percentChange = (current: number, baseline: number): number | null =>
  baseline > 0 ? ((current - baseline) / baseline) * 100 : null;

const absoluteChange = (current: number, baseline: number, hasBaseline: boolean): number | null =>
  hasBaseline ? current - baseline : null;

/**
 * Derives the four Pending Loads headline numbers plus their 30-day movement.
 * Everything is computed from the invoices in hand — no history is stored.
 */
export const computePendingMetrics = (
  invoices: MetricInvoice[],
  now: number = Date.now()
): PendingMetrics => {
  const cutoff = now - 30 * DAY_MS;

  const current = invoices.filter(isPending);
  const baseline = pendingAsOf(invoices, cutoff);
  // With nothing on the books 30 days ago there is no honest comparison to draw.
  const hasBaseline = baseline.length > 0;

  return {
    totalPendingAmount: {
      value: sumCharges(current),
      delta: percentChange(sumCharges(current), sumCharges(baseline)),
      deltaKind: 'percent',
      riseIsGood: true
    },
    pendingInvoiceCount: {
      value: current.length,
      delta: absoluteChange(current.length, baseline.length, hasBaseline),
      deltaKind: 'absolute',
      riseIsGood: true
    },
    driversAwaitingPayout: {
      value: distinctDrivers(current),
      delta: absoluteChange(distinctDrivers(current), distinctDrivers(baseline), hasBaseline),
      deltaKind: 'absolute',
      riseIsGood: true
    },
    averageAgingDays: {
      value: Math.round(averageAging(current, now)),
      delta: absoluteChange(
        Math.round(averageAging(current, now)),
        Math.round(averageAging(baseline, cutoff)),
        hasBaseline
      ),
      deltaKind: 'absolute',
      // Invoices ageing faster is a worse position to be in.
      riseIsGood: false
    }
  };
};
